/**
 * interact_input.ts — ONE answer to "did the player press Interact?" (client sweep 2026-10-05, F2/G6).
 *
 * Nine modules tested the physical `e.code === 'KeyE'`. Interact is a REBINDABLE action (`keybinds.ts`) and a pad's
 * (or touch's) interact reaches the world only as the bus topic `atlas:place-requested` (what slice H taught the
 * interiors). A gamepad player, or anyone who rebound Interact, saw "E — cast a line" and could not do it.
 *
 *   onInteract(fn)        hears the BOUND key AND the pad. The input module emits `atlas:place-requested` for the
 *                         keyboard's interact too, so the two arrivals of one press are folded (INTERACT_DEDUPE_MS).
 *   isInteractKeyDown(e)  the keyboard half, for a handler that needs the event itself (hold-to-reel).
 *   interactHeld(held)    is Interact held — the bound key in the caller's own key set, or the pad button.
 *   interactPrompt(text)  "<bound key> — text": a prompt that follows a rebind.
 *
 * The ONE KEY, ONE FOCUS arbiter (lib/interact_focus.ts) is untouched: a handler still asks `interactFocus().owns()`.
 */
import { keyIs, keyHint, keyHeldIn } from './keybinds.js';
import { padHeld } from './gamepad.js';
import { bus } from '../bus.js';

/** The keyboard and the bus event are the same press when they land this close. */
export const INTERACT_DEDUPE_MS = 120;

/** Folds the key's two arrivals (keydown + the input module's bus emit) into one press. Pure. */
export class InteractDedupe {
  private last = -1e9;
  accept(nowMs: number): boolean {
    if (nowMs - this.last < INTERACT_DEDUPE_MS) return false;
    this.last = nowMs;
    return true;
  }
}

/** A focused text field owns the keystroke (and a pad press while typing is not an interact either). */
export function typingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable === true;
}

/** Is this keydown the bound Interact (a fresh press, no Ctrl/Meta chord, not typing)? */
export function isInteractKeyDown(e: Pick<KeyboardEvent, 'code' | 'repeat' | 'ctrlKey' | 'metaKey' | 'target'>): boolean {
  return keyIs(e.code, 'interact') && !e.repeat && !e.ctrlKey && !e.metaKey && !typingTarget(e.target);
}

/** Subscribe to Interact presses (bound key + pad). Returns the unsubscribe. */
export function onInteract(fn: () => void): () => void {
  const dedupe = new InteractDedupe();
  const fire = (): void => { if (dedupe.accept(performance.now())) fn(); };
  const onKey = (e: KeyboardEvent): void => { if (isInteractKeyDown(e)) fire(); };
  window.addEventListener('keydown', onKey);
  const off = bus.on('atlas:place-requested', () => { if (!typingTarget(document.activeElement)) fire(); });
  return () => { window.removeEventListener('keydown', onKey); if (typeof off === 'function') off(); };
}

/** Is Interact held — the bound key (in the caller's held-code set) or the pad's interact button? */
export function interactHeld(held: ReadonlySet<string>): boolean {
  return keyHeldIn(held, 'interact') || padHeld('interact');
}

/** The bound key's label ("E", or whatever it was rebound to; "unbound" when empty). */
export function interactKey(): string { return keyHint('interact'); }

/** "<key> — text" with the CURRENT binding. */
export function interactPrompt(text: string): string { return `${keyHint('interact')} — ${text}`; }
